import React, { useState } from 'react';
import { FaChevronDown, FaChevronUp, FaStar } from 'react-icons/fa';
import { HiSpeakerWave, HiOutlineSpeakerWave } from 'react-icons/hi2';
import { ImSpinner8 } from 'react-icons/im';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import "katex/dist/katex.min.css";
import { InlineMath, BlockMath } from "react-katex";
import rehypeRaw from "rehype-raw";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";

const compactProseStyles = `
  /* Aggressively reduce margins for all text elements */
  .compact-prose :where(p):not(:where([class~=not-prose] *)) {
    margin-top: 0.15em !important;
    margin-bottom: 0.15em !important;
    line-height: 1.5 !important;
  }

  /* Minimal spacing for headings */
  .compact-prose :where(h1, h2, h3, h4, h5, h6):not(:where([class~=not-prose] *)) {
    margin-top: 0.4em !important;
    margin-bottom: 0.2em !important;
    line-height: 1.3 !important;
  }

  /* Compact lists with minimal spacing */
  .compact-prose :where(ul, ol):not(:where([class~=not-prose] *)) {
    margin-top: 0.15em !important;
    margin-bottom: 0.15em !important;
    padding-left: 1.5em !important;
  }

  /* Minimal list item spacing */
  .compact-prose :where(li):not(:where([class~=not-prose] *)) {
    margin-top: 0 !important;
    margin-bottom: 0.05em !important;
    line-height: 1.4 !important;
  }

  /* Nested lists - even more compact */
  .compact-prose :where(li > ul, li > ol):not(:where([class~=not-prose] *)) {
    margin-top: 0.1em !important;
    margin-bottom: 0.1em !important;
  }

  /* Horizontal rules */
  .compact-prose :where(hr):not(:where([class~=not-prose] *)) {
    margin-top: 0.5em !important;
    margin-bottom: 0.5em !important;
  }

  /* Blockquotes */
  .compact-prose :where(blockquote):not(:where([class~=not-prose] *)) {
    margin-top: 0.3em !important;
    margin-bottom: 0.3em !important;
    padding-top: 0.3em !important;
    padding-bottom: 0.3em !important;
  }

  /* Code blocks and pre */
  .compact-prose :where(pre, code):not(:where([class~=not-prose] *)) {
    margin-top: 0.2em !important;
    margin-bottom: 0.2em !important;
  }

  /* Remove spacing from first/last children */
  .compact-prose > :first-child,
  .compact-prose .explanation > :first-child {
    margin-top: 0 !important;
  }

  .compact-prose > :last-child,
  .compact-prose .explanation > :last-child {
    margin-bottom: 0 !important;
  }

  /* Overall line height for readability without excessive spacing */
  .compact-prose {
    line-height: 1.5 !important;
  }
`;

const CollapsibleSection = ({ title, items, isOpen, toggleOpen, onItemClick, isClickable }) => {
  if (!items || items.length === 0) return null;

  // Ensure all items are strings
  const stringItems = items.map(item => typeof item === 'string' ? item : String(item || ''));

  // Process LaTeX formatting in each item
  const processEquation = (equation) => {
    // Process content to handle various LaTeX formats
    let processedContent = equation;

    // Replace double backslashes with single backslashes for LaTeX rendering
    processedContent = processedContent.replace(/\\\\/g, '\\');

    // Ensure the equation is wrapped in dollar signs for proper math rendering
    if (!processedContent.startsWith('$') && !processedContent.endsWith('$')) {
      processedContent = `$${processedContent}$`;
    }

    return processedContent;
  };

  return (
    <div className="mt-3">
      <button
        onClick={toggleOpen}
        className="flex items-center text-indigo-600 hover:text-indigo-800
                  bg-indigo-50 hover:bg-indigo-100 rounded-full px-4 py-2
                  transition-all duration-200 text-sm font-medium"
      >
        {isOpen ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
        {title} <FaStar className="ml-2 text-yellow-400" />
      </button>
      {isOpen && (
        <div className="mt-2 pl-4 border-l-4 border-indigo-200 space-y-2">
          {stringItems.map((item, index) => (
            <div
              key={index}
              className={`p-2 rounded-lg ${
                isClickable
                  ? 'bg-blue-50 hover:bg-blue-100 cursor-pointer transition-colors duration-200'
                  : 'bg-purple-50'
              }`}
              onClick={() => isClickable && onItemClick(item)}
              role={isClickable ? "button" : "none"}
            >
              {isClickable ? (
                item
              ) : (
                <ReactMarkdown
                  remarkPlugins={[remarkMath]}
                  rehypePlugins={[rehypeKatex, rehypeRaw]}
                  components={{
                    inlineMath: ({ node, ...props }) => <InlineMath math={String(props.value || '')} />,
                    math: ({ node, ...props }) => <BlockMath math={String(props.value || '')} />
                  }}
                >
                  {processEquation(item)}
                </ReactMarkdown>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const AssistantMessage = ({
  message,
  onFollowUpClick,
  formToken,
  chatId,
  isLastMessage,
  canTurnIntoQuiz,
  onTurnIntoQuiz,
  isLoading
}) => {
  const [audioStatus, setAudioStatus] = useState('not_generated');
  const [audioUrl, setAudioUrl] = useState(message.audio_url);
  const [showEquations, setShowEquations] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  // Only show audio for language learning chats
  const showAudioButton = message.chat_type === 'language_learning';

  // Only show map focus for map explorer chats
  const showMapFocus = message.chat_type === 'map_explorer' && message.map_focus;

  const handleGenerateAudio = async () => {
    setAudioStatus('generating');
    try {
      const response = await axios.post(
        `/api/v1/chat_messages/${message.id}/generate_audio`,
        {},
        {
          headers: {
            "Authorization": `Bearer ${formToken}`,
          },
        }
      );
      setAudioUrl(response.data.audio_url);
      setAudioStatus('ready');
    } catch (error) {
      console.error("Error generating audio:", error);
      setAudioStatus('error');
    }
  };

  const renderContent = (content) => {
    try {
      // Ensure content is a string
      const stringContent = typeof content === 'string' ? content : String(content || '');

      // Process content to handle various LaTeX formats
      let processedContent = stringContent.replace(
        /\\\\(\(|\)|\[|\])/g,  // Match \\( \\) \\[ \\]
        (match, bracket) => bracket  // Replace with just the bracket
      );

      processedContent = processedContent.replace(
        /\(\\([^)]+)\)/g,
        (match, p1) => `$${p1}$`
      );

      processedContent = processedContent.replace(
        /\\\\\\\\(\(|\))\\\\([^\\]+)\\\\\\\\(\(|\))/g,
        (match, openBracket, content, closeBracket) => `$${content}$`
      );

      // Helper function to wrap LaTeX in dollar signs if needed
      const wrapLatexInDollarSigns = (text) => {
        // Split text by lines to process each line separately
        const lines = text.split('\n');
        return lines.map(line => {
          // If line has LaTeX commands but no dollar sign delimiters
          if ((line.includes('\\frac') ||
               line.includes('\\alpha') ||
               line.includes('\\beta') ||
               line.includes('\\gamma') ||
               line.includes('\\sqrt')) &&
              !line.includes('$')) {

            // Simple heuristic: If the entire line is a LaTeX expression
            if (line.trim().startsWith('\\') && !line.includes(':')) {
              return `$${line}$`;
            }
          }
          return line;
        }).join('\n');
      };

      // Apply dollar sign wrapping only to standalone latex expressions
      processedContent = wrapLatexInDollarSigns(processedContent);
      return (
        <ReactMarkdown
          remarkPlugins={[remarkMath]}
          rehypePlugins={[rehypeRaw, rehypeKatex]}
          components={{
            // Handle inline math expressions with InlineMath component
            inlineMath: ({value}) => {
              try {
                if (typeof value !== 'string') {
                  return <span>{String(value || '')}</span>;
                }
                return <InlineMath math={value} />;
              } catch (error) {
                console.error('Production Debug - Error in InlineMath:', error);
                return <span>{String(value || '')}</span>;
              }
            },
            // Handle block math expressions with BlockMath component
            math: ({value}) => {
              try {
                if (typeof value !== 'string') {
                  return <span className="p-2 bg-gray-50 rounded">{String(value || '')}</span>;
                }
                return <BlockMath math={value} />;
              } catch (error) {
                return <span className="p-2 bg-gray-50 rounded">{String(value || '')}</span>;
              }
            }
          }}
        >
          {processedContent}
        </ReactMarkdown>
      );
    } catch (error) {
      console.error("Error rendering markdown:", error);
      // Fallback to plain text if markdown parsing fails
      return <p>{typeof content === 'string' ? content : String(content || '')}</p>;
    }
  };

  return (
    <div className="bg-gradient-to-br from-white to-indigo-50 rounded-xl p-6 shadow-lg border border-indigo-100">
      <style dangerouslySetInnerHTML={{ __html: compactProseStyles }} />

      {/* Confidence Banner */}
      {typeof message.confidence === 'number' && message.confidence < 0.8 && (
        <div className="mb-4 p-3 bg-yellow-100 border border-yellow-300 text-yellow-800 rounded-lg text-center">
          <p className="font-medium">⚠️ I'm pretty sure this is right, but let's double-check together!</p>
        </div>
      )}

      <div className="prose max-w-none compact-prose">
        <div className="flex justify-between items-start mb-4">
          <div className="text-gray-800 whitespace-pre-wrap leading-relaxed flex-grow">
            {/* Render main content using message.content */}
            {message.content && (
              <div className="explanation"> {/* Keep class if styling relies on it */}
                {renderContent(message.content)}
              </div>
            )}
          </div>
          {showAudioButton && (
            <div className="ml-4 flex-shrink-0">
              {audioUrl ? (
                <div className="bg-indigo-50 rounded-lg p-2">
                  <audio
                    controls
                    src={audioUrl}
                    className="h-8 w-32"
                  />
                </div>
              ) : (
                <button
                  onClick={handleGenerateAudio}
                  className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800
                           bg-indigo-50 hover:bg-indigo-100 rounded-full px-4 py-2
                           transition-all duration-200 text-sm font-medium"
                  disabled={audioStatus === 'generating'}
                >
                  {audioStatus === 'generating' ? (
                    <>
                      <ImSpinner8 className="animate-spin h-5 w-5" />
                      <span>Reading...</span>
                    </>
                  ) : (
                    <>
                      <HiSpeakerWave className="h-5 w-5" />
                      <span>Hear it!</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        <CollapsibleSection
          title="Helpful Equations to break it down."
          items={message.equations}
          isOpen={showEquations}
          toggleOpen={() => setShowEquations(!showEquations)}
          isClickable={false}
        />

        <CollapsibleSection
          title="Try These Other Questions to Learn More."
          items={message.follow_up}
          isOpen={showFollowUp}
          toggleOpen={() => setShowFollowUp(!showFollowUp)}
          onItemClick={onFollowUpClick}
          isClickable={true}
        />

        {showMapFocus && (
          <div className="mt-4">
            <p className="text-sm text-gray-500">
              Map focused on: {message.map_focus.latitude}, {message.map_focus.longitude}
            </p>
          </div>
        )}

        {/* Generate Quiz button - only show on last assistant message */}
        {isLastMessage && canTurnIntoQuiz && (
          <div className="mt-4 pt-3 border-t border-indigo-200 flex justify-end">
            <button
              onClick={onTurnIntoQuiz}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 text-white font-medium rounded-md transition-colors duration-200 shadow-sm text-xs"
            >
              <span className="mr-1.5">📝</span>
              Generate Quiz
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssistantMessage;
